/**
 * KAN-931: аккаунты мессенджера MAX (вкладка "MAX" в настройках рядом с
 * Telegram-аккаунтами): список с переключателем "включён" и подключение
 * токеном (token, device_id, тип клиента, прокси по желанию).
 * Токен после отправки стирается из формы и нигде не показывается.
 */
import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Switch } from '@/components/ui/switch';
import { useMaxAccounts } from '@/hooks/useMaxAccounts';
import type { MaxAccount, MaxClientKind } from '@/services/api/maxAccounts';

const KIND_OPTIONS: Array<{ value: MaxClientKind; label: string }> = [
  { value: 'android', label: 'Android' },
  { value: 'web', label: 'Web' },
];

const STATUS_LABELS: Record<string, string> = {
  connected: 'Подключён',
  dead: 'Не работает',
};

/** Имя аккаунта, иначе телефон, иначе id. */
function accountName(account: MaxAccount): string {
  const name = [account.first_name, account.last_name]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return name || (account.phone ?? '').trim() || account.id;
}

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export function MaxAccountsSection() {
  const { canList, canImport, canToggle, accounts, importToken, setEnabled } = useMaxAccounts();
  const [token, setToken] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [kind, setKind] = useState<MaxClientKind>('android');
  const [proxy, setProxy] = useState('');

  if (!canList) return null;

  const canSubmit = !!token.trim() && !!deviceId.trim() && !importToken.isPending;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    importToken.mutate(
      { token: token.trim(), device_id: deviceId.trim(), kind, proxy: proxy.trim() },
      {
        onSuccess: () => {
          setToken('');
          setDeviceId('');
          setProxy('');
        },
      }
    );
  };

  const list = accounts.data ?? [];

  return (
    <section className="space-y-6" data-testid="max-accounts-section">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Аккаунты MAX</h2>
        <p className="mt-1 text-sm text-gray-500">
          С этих аккаунтов бот и менеджеры переписываются с клиентами в MAX.
        </p>
      </div>

      {accounts.isLoading && <p className="text-sm text-gray-500">Загрузка...</p>}
      {accounts.isError && (
        <p className="text-sm text-red-600">Не удалось загрузить аккаунты MAX</p>
      )}
      {accounts.isSuccess && list.length === 0 && (
        <p className="text-sm text-gray-500">Аккаунтов MAX пока нет</p>
      )}
      {list.length > 0 && (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
          {list.map((account) => (
            <li
              key={account.id}
              data-testid={`max-account-row-${account.id}`}
              className="flex items-center justify-between gap-3 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{accountName(account)}</p>
                <p className="text-xs text-gray-500">{statusLabel(account.status)}</p>
              </div>
              <Switch
                checked={account.enabled}
                disabled={!canToggle || setEnabled.isPending}
                onCheckedChange={(enabled) => setEnabled.mutate({ id: account.id, enabled })}
                aria-label={`Аккаунт ${accountName(account)} включён`}
                data-testid={`max-account-enabled-${account.id}`}
              />
            </li>
          ))}
        </ul>
      )}

      {canImport && (
        <form onSubmit={submit} className="space-y-3 rounded-md border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900">Подключить аккаунт токеном</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="max-import-token">Токен</Label>
              <Input
                id="max-import-token"
                type="password"
                autoComplete="off"
                value={token}
                onChange={(event) => setToken(event.target.value)}
                data-testid="max-import-token"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="max-import-device-id">ID устройства</Label>
              <Input
                id="max-import-device-id"
                autoComplete="off"
                value={deviceId}
                onChange={(event) => setDeviceId(event.target.value)}
                data-testid="max-import-device-id"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="max-import-kind">Клиент</Label>
              <NativeSelect
                id="max-import-kind"
                value={kind}
                onChange={(event) => setKind(event.target.value as MaxClientKind)}
                options={KIND_OPTIONS}
                data-testid="max-import-kind"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="max-import-proxy">Прокси (необязательно)</Label>
              <Input
                id="max-import-proxy"
                autoComplete="off"
                placeholder="socks5://host:port"
                value={proxy}
                onChange={(event) => setProxy(event.target.value)}
                data-testid="max-import-proxy"
              />
            </div>
          </div>
          <Button type="submit" disabled={!canSubmit} data-testid="max-import-submit">
            {importToken.isPending ? 'Подключаем...' : 'Подключить'}
          </Button>
        </form>
      )}
    </section>
  );
}
